import { useEffect, useState } from 'react'
import Link from 'next/link'
import cn from 'classnames'
import LogoInverted from 'components/ui/logotype/logotype-inverted.svg'

import { useRouter } from 'next/router'

import { ReactSVG } from 'react-svg'

import { useTranslation } from 'utils/use-translation'

import LogoDark from './icons/logo_dark.svg'

import IconTwitter from './icons/twitter.svg'
import IconTelegram from './icons/telegram.svg'
import IconLinkedIn from './icons/linkedin.svg'
import IconYoutube from './icons/youtube.svg'

import styles from './styles.module.scss'

const Footer = () => {
  const { t, replacePlaceholders } = useTranslation()

  const [isDark, setIsDark] = useState(false)
  const router = useRouter()

  useEffect(() => {
    if (router) {
      setIsDark(router.pathname.includes('private-clients') ||
        router.pathname.includes('Consensus2025_main') ||
        router.pathname.includes('Event')
      )
    }
  }, [router])

  const loanLinks = [
    ['/loans/btc', 'Bitcoin (BTC)'],
    ['/loans/ltc', 'Litecoin (LTC)'],
    ['/loans/eth', 'Ethereum (ETH)'],
    ['/coin/mog', 'Mog Coin (MOG)'],
    ['/loans/xrp', 'XRP (XRP)'],
    ['/loans/dash', 'Dash (DASH)'],
    ['/loans/sol', 'Solana (SOL)'],
    ['/loans/uni', 'Uniswap (UNI)'],
    ['/loans/usdt', 'Tether (USDT)'],
    ['/loans/vet', 'VeChain (VET)'],
    ['/loans/usdc', 'USD Coin (USDC)'],
    ['/coin/kaia', 'Kaia (KAIA)'],
    ['/loans/ada', 'Cardano (ADA)'],
    ['/loans/doge', 'Doge (DOGE)'],
    ['/loans/aave', 'Aave (AAVE)'],
    ['/', 'loan_calculator'],
     ['/loans/dot', 'Polkadot (DOT)'],
  ]

  const walletLinks = [
    ['/wallet-coin/btc', 'Bitcoin (BTC)'],
    ['/wallet/dash', 'Dash (DASH)'],
    ['/wallet/xdc', 'XinFin Network (XDC)'],
    ['/wallet/usdc', 'USD Coin (USDC)'],
    ['/wallet/ltc', 'Litecoin (LTC)'],
    ['/wallet/dot', 'Polkadot (DOT)'],
    ['/wallet/ftm', 'Fantom (FTM)'],
    ['/wallet/eth', 'Ethereum (ETH)'],
    ['/wallet/theta', 'Theta Network (THETA)'],
    ['/wallet/xrp', 'XRP (XRP)'],
    ['/wallet/kaspa', 'Kaspa (KASPA)'],
    ['/wallet/hbar', 'Hedera (HBAR)'],
    ['/wallet/sui', 'Sui (SUI)'],
    ['/wallet/sol', 'Solana (SOL)'],
    ['/wallet/link', 'Chainlink (LINK)'],
    ['/wallet', 'choose_your_wallet'],
    ['/wallet/avax', 'Avalanche (AVAX)'],
  ]

  const exchangeLinks = [
    ['/btc-ltc', ['BTC', 'LTC']],
    ['/sol-eth', ['SOL', 'ETH']],
    ['/btc-sol', ['BTC', 'SOL']],
    ['/sol-btc', ['SOL', 'BTC']],
    ['/btc-trx', ['BTC', 'TRX']],
    ['/ltc-xmr', ['LTC', 'XMR']],
    ['/btc-eth', ['BTC', 'ETH']],
    ['/usdt-eth', ['USDT', 'ETH']],
    ['/btc-usdt', ['BTC', 'USDT']],
    ['/xmr-ltc', ['XMR', 'LTC']],
    ['/btc-bnb', ['BTC', 'BNB']],
    ['/xmr-eth', ['XMR', 'ETH']],
    ['/btc-xrp', ['BTC', 'XRP']],
    ['/xmr-usdt', ['XMR', 'USDT']],
    ['/bnb-usdt', ['BNB', 'USDT']],
    ['/', ['choose_your_pair', '']],
    ['/eth-usdt', ['ETH', 'USDT']],
  ]

  const companyLinks = [
    ['terms-of-use', 'terms_of_use'],
    ['privacy-policy', 'privacy_policy'],
    ['blog', 'blog'],
  ]

  const partnerLinks = [
    ['partners', 'partner_with_us'],
    ['referral', 'referral_program'],
  ]

  return (
    <div className={cn(styles.footer, { [styles.footer_vip]: isDark })}>
      <div className={cn('container', styles.footerContainer)}>
        <div className={styles.rightBlock}>
          <div className={styles.legalInfo}>
            <div className={styles.copyright}>
              <Link prefetch={false} href="/">
                <a>
                  <img src={isDark ? LogoDark : LogoInverted} alt="CoinRabbit" width={123} height={25} loading="lazy" />
                </a>
              </Link>
              <p className={styles.copyrightText}>{new Date().getFullYear()} © Coinrabbit</p>
            </div>
          </div>

          <div className={styles.column}>
            <p>{t('crypto_loans')}</p>

            <div className={styles.innerColumn}>
              {loanLinks.map(([symbol, translationKey]) => (
                <Link key={symbol} prefetch={false} href={`${symbol}`}>
                  <a>{symbol !== '/' ? replacePlaceholders(t('code_loan'), {
                    code: translationKey
                  }) : t(translationKey)}</a>
                </Link>
              ))}
            </div>
          </div>

          <div className={styles.column}>
            <p>{t('crypto_wallet')}</p>

            <div className={styles.innerColumn}>
              {walletLinks.map(([symbol, translationKey]) => (
                <Link key={symbol} prefetch={false} href={`${symbol}`}>
                  <a>{symbol !== '/wallet' ? replacePlaceholders(t('code_wallet'), {
                    code: translationKey
                  }) : t(translationKey)}</a>
                </Link>
              ))}
            </div>
          </div>

          <div className={cn(styles.column, styles['second-row-item'])}>
            <p>{t('exchange_crypto')}</p>

            <div className={styles.innerColumn}>
              {exchangeLinks.map(([symbol, [from, to]]) => (
                <Link key={`${from}-${to}`} prefetch={false} href={`/exchange${symbol}`}>
                  <a>{symbol !== '/' ? replacePlaceholders(t('code_to_code'), {
                    from: from,
                    to: to
                  }) : t(from)}</a>
                </Link>
              ))}
            </div>
          </div>

          <div className={styles.column}>
            <div className={styles.socialsBox}>
              <a
                className={styles.socialLink}
                href="https://twitter.com/CoinRabbitLoans"
                title="x link"
                aria-label="go-to-x"
                rel="noopener noreferrer"
                target="_blank"
              >
                <ReactSVG className={styles.icon} src={IconTwitter} width={20} height={20} />
              </a>

              <a
                className={styles.socialLink}
                href="https://t.me/+vKoWSNIXLbZmZTQy"
                title="telegram link"
                aria-label="go-to-telegram"
                rel="noopener noreferrer"
                target="_blank"
              >
                <ReactSVG className={styles.icon} src={IconTelegram} width={20} height={20} />
              </a>

              <a
                className={styles.socialLink}
                href="https://www.linkedin.com/company/80426861/admin/page-posts/published/"
                title="linkedIn link"
                aria-label="go-to-linkedin"
                rel="noopener noreferrer"
                target="_blank"
              >
                <ReactSVG className={styles.icon} src={IconLinkedIn} width={20} height={20} />
              </a>

              <a
                className={styles.socialLink}
                href="https://www.youtube.com/@coinrabbit_loans"
                title="youtube link"
                aria-label="go-to-youtube"
                rel="noopener noreferrer"
                target="_blank"
              >
                <ReactSVG className={styles.icon} src={IconYoutube} width={20} height={20} />
              </a>
            </div>

            <div className={styles.partners}>
              <div>
                <p>{t('company')}</p>

                <div className={cn(styles.column, styles.company)}>
                  {companyLinks.map(([symbol, translationKey]) => (
                    <Link key={symbol} prefetch={false} href={`/${symbol}`}>
                      <a>{t(translationKey)}</a>
                    </Link>
                  ))}
                </div>
              </div>

              <div>
                <p>{t('for_partners')}</p>

                <div className={styles.column}>
                  {partnerLinks.map(([symbol, translationKey]) => (
                    <Link key={symbol} prefetch={false} href={`/${symbol}`}>
                      <a>{t(translationKey)}</a>
                    </Link>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      <div className={cn('container', styles.footerContainer)}>
        <p className={styles.footerText}>{t('liability_notice')}</p>
      </div>
    </div>
  )
}

export default Footer
